import type { ReactNode } from 'react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { createContext, useContext, useState } from 'react';
import { ESUSU_BASE_URL, getPublicKeyForEnv } from '../utils/esusu';
import { useAuth } from '../AuthProvider';

interface EsusuContextType {
  esusuSdk: unknown | null;
  encodedZuid: string | null;
  setShouldInitializeEsusu: (shouldInitialize: boolean) => void;
  setIsEsusuScriptLoaded: (isEsusuScriptLoaded: boolean) => void;
  isEsusuScriptLoaded: boolean;
  markContainerAsInitialized: (containerId: string) => void;
  isContainerInitialized: (containerId: string) => boolean;
  removeContainerFromInitialized: (containerId: string) => void;
}

const EsusuContext = createContext<EsusuContextType | undefined>(undefined);

export const useEsusu = () => {
  const context = useContext(EsusuContext);
  if (context === undefined) {
    throw new Error('useEsusu must be used within an EsusuProvider');
  }
  return context;
};

interface EsusuProviderProps {
  children: ReactNode;
}

export const EsusuProvider: React.FC<EsusuProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [esusuSdk, setEsusuSdk] = useState<unknown | null>(null);
  const [shouldInitializeEsusu, setShouldInitializeEsusu] = useState(false);
  const [isEsusuScriptLoaded, setIsEsusuScriptLoaded] = useState(false);

  const initializedContainersRef = useRef<Set<string>>(new Set());

  /**
   * Removes X1- prefix from ZUID to match Esusu SDK requirements.
   * Your Rental Web receives ZUIDs with X1- prefix that must be stripped.
   */
  const stripX1Prefix = (zuid: string | undefined) => {
    if (!zuid) return null;
    return zuid.startsWith('X1-') ? zuid.substring(3) : zuid;
  };

  const encodedZuid = stripX1Prefix(user?.encodedZuid);

  const initializeEsusu = (encodedZuid: string) => {
    if (typeof window !== 'undefined' && window.Esusu) {
      const esusuKey = getPublicKeyForEnv();
      const sdk = new window.Esusu({
        publicKey: esusuKey,
        referenceId: encodedZuid,
        language: 'en',
        baseUrl: ESUSU_BASE_URL,
        refreshSession: false,
        deepLinking: true,
      });
      setEsusuSdk(sdk);
    }
  };

  useEffect(() => {
    if (encodedZuid && shouldInitializeEsusu) {
      initializeEsusu(encodedZuid);
    }
  }, [shouldInitializeEsusu, encodedZuid]);

  // When SDK is recreated (e.g., user changes), reset initialized containers
  useEffect(() => {
    if (esusuSdk === null) {
      initializedContainersRef.current = new Set();
    }
  }, [esusuSdk]);

  // Memoize callbacks to prevent unnecessary effect re-runs in consumers
  const markContainerAsInitialized = useCallback((containerId: string) => {
    initializedContainersRef.current.add(containerId);
  }, []);

  const isContainerInitialized = useCallback((containerId: string) => {
    return initializedContainersRef.current.has(containerId);
  }, []);

  const removeContainerFromInitialized = useCallback((containerId: string) => {
    initializedContainersRef.current.delete(containerId);
  }, []);

  const value: EsusuContextType = {
    esusuSdk,
    encodedZuid: encodedZuid,
    setShouldInitializeEsusu,
    setIsEsusuScriptLoaded,
    isEsusuScriptLoaded,
    markContainerAsInitialized,
    isContainerInitialized,
    removeContainerFromInitialized,
  };

  return <EsusuContext.Provider value={value}>{children}</EsusuContext.Provider>;
};
